import type { MatchBestOf, MatchFormat, MatchMode } from '../types/match';

export interface MatchFormatOption {
  value: MatchFormat;
  label: string;
  /** Compact label for space-constrained chrome such as the play HUD. */
  shortLabel: string;
  helper: string;
  matchMode: MatchMode;
  playerCount: 2 | 4;
  recommendedBestOf: MatchBestOf;
  recommendedTargetScore: number;
  battlefieldCount: 2 | 3;
  firstPlayerDraws: boolean;
}

export const MATCH_FORMAT_OPTIONS: MatchFormatOption[] = [
  {
    value: 'casual',
    label: 'Casual / Nexus Night',
    shortLabel: 'Casual',
    helper: 'Store preference, kitchen table, weekly casual play',
    matchMode: 'standard1v1',
    playerCount: 2,
    recommendedBestOf: 3,
    recommendedTargetScore: 8,
    battlefieldCount: 2,
    firstPlayerDraws: true,
  },
  {
    value: 'sealed',
    label: 'Prerelease / Sealed',
    shortLabel: 'Sealed',
    helper: 'Limited events where players build from opened product',
    matchMode: 'standard1v1',
    playerCount: 2,
    recommendedBestOf: 1,
    recommendedTargetScore: 8,
    battlefieldCount: 2,
    firstPlayerDraws: true,
  },
  {
    value: 'constructed',
    label: '1v1 Constructed',
    shortLabel: 'Constructed',
    helper: 'Summoner Skirmish, Regional Qualifier, Championship, Worlds',
    matchMode: 'standard1v1',
    playerCount: 2,
    recommendedBestOf: 3,
    recommendedTargetScore: 8,
    battlefieldCount: 2,
    firstPlayerDraws: true,
  },
  {
    value: 'side-event',
    label: '4-player Free-for-All',
    shortLabel: 'Side event',
    helper: 'Casual multiplayer table: 3 random battlefields, first player does not draw',
    matchMode: 'freeForAll4',
    playerCount: 4,
    recommendedBestOf: 1,
    recommendedTargetScore: 8,
    battlefieldCount: 3,
    firstPlayerDraws: false,
  },
  {
    value: 'two-vs-two',
    label: 'Magma Chamber',
    shortLabel: 'Magma',
    helper: '2v2 team format: shared VP to 11, first player does not draw',
    matchMode: 'twoVsTwo',
    playerCount: 4,
    recommendedBestOf: 1,
    recommendedTargetScore: 11,
    battlefieldCount: 3,
    firstPlayerDraws: false,
  },
];

/** The only supported match lengths. Single source for every best-of picker. */
export const BEST_OF_OPTIONS: readonly MatchBestOf[] = [1, 3, 5];

export function bestOfLabel(bestOf: MatchBestOf): string {
  return `Best of ${bestOf}`;
}

export const BEST_OF_CHOICES: readonly { value: MatchBestOf; label: string; helper: string }[] =
  BEST_OF_OPTIONS.map((value) => ({
    value,
    label: bestOfLabel(value),
    helper: value === 1 ? 'Single game' : `First to ${Math.ceil(value / 2)} games`,
  }));

export const MATCH_FORMAT_LABELS: Record<string, string> = MATCH_FORMAT_OPTIONS.reduce<Record<string, string>>(
  (labels, option) => {
    labels[option.value] = option.label;
    return labels;
  },
  {},
);

export function getMatchFormatLabel(format: string | null | undefined): string {
  if (!format) return MATCH_FORMAT_LABELS.casual;
  return MATCH_FORMAT_LABELS[format] ?? format;
}

export function matchFormatOptionFor(format: string | null | undefined): MatchFormatOption {
  return MATCH_FORMAT_OPTIONS.find((option) => option.value === format) ?? MATCH_FORMAT_OPTIONS[0];
}

export function matchModeForFormat(format: string | null | undefined): MatchMode {
  return matchFormatOptionFor(format).matchMode;
}

/** Table geometry — the first choice in match setup, before 1v1 flavor. */
export const MATCH_TABLE_OPTIONS: readonly { value: MatchMode; label: string; helper: string }[] = [
  { value: 'standard1v1', label: '1v1', helper: 'Two players, two battlefields' },
  { value: 'freeForAll4', label: 'Free-for-All', helper: 'Four seats, three battlefields' },
  { value: 'twoVsTwo', label: 'Magma Chamber', helper: '2v2 teams, shared VP to 11' },
];

export const ONE_V_ONE_FORMAT_OPTIONS: MatchFormatOption[] = MATCH_FORMAT_OPTIONS.filter(
  (option) => option.matchMode === 'standard1v1',
);

const DEFAULT_FORMAT_FOR_MATCH_MODE: Record<MatchMode, MatchFormat> = {
  standard1v1: 'casual',
  freeForAll4: 'side-event',
  twoVsTwo: 'two-vs-two',
};

/** Keep the current format when it already matches the table; otherwise the mode default. */
export function preferredFormatForMatchMode(mode: MatchMode, currentFormat: MatchFormat): MatchFormat {
  if (matchModeForFormat(currentFormat) === mode) return currentFormat;
  return DEFAULT_FORMAT_FOR_MATCH_MODE[mode];
}

export function isFormatRulesDefault(
  format: string | null | undefined,
  bestOf: MatchBestOf,
  targetScore: number,
): boolean {
  const option = matchFormatOptionFor(format);
  return option.recommendedBestOf === bestOf && option.recommendedTargetScore === targetScore;
}

export function matchRulesSummary(
  format: string | null | undefined,
  bestOf: MatchBestOf,
  targetScore: number,
): string {
  const option = matchFormatOptionFor(format);
  return `${option.shortLabel} · Best of ${bestOf} · ${targetScore} points`;
}
